import {
    DEFAULT_LOCALE,
    getHostDefaultLocale,
    normalizeLocale,
} from '../data/static/locale-config.js'
import {
    buildPathnameForLocale,
    getPathWithoutLocalePrefix,
    pathnameHasLocalePrefix,
} from './locale-path.js'
import {
    readLocalePreference,
    writeLocalePreference,
} from '../../stores/locale-currency-preference-store.js'

/**
 * Redirect an unprefixed URL to the right locale before the Solid tree mounts.
 * Precedence:
 *   1. `?locale=` query param (fresh OAuth /complete-profile signal) — wins and is persisted.
 *   2. Saved preference (localStorage), including an explicit `en` which blocks the host default.
 *   3. Host default (e.g. lodgr.mx → es-mx).
 * No-op when already prefixed or the resolved target is the default (`en`).
 *
 * Uses window.location so it can run before the Solid tree mounts. `?simulateHost=`
 * always forces the host default so local testing is not blocked by a stored `en`.
 * The in-app SPA equivalent lives in the LocaleProvider effect (locale-context.jsx).
 */
export function redirectToPreferredOrHostLocale () {
    const {
        pathname,
        search,
        hash,
    } = window.location

    if (pathnameHasLocalePrefix(pathname)) {
        return
    }

    const parameters = new URLSearchParams(search || '')
    const queryLocale = normalizeLocale(parameters.get('locale'))
    if (queryLocale) {
        writeLocalePreference(queryLocale)
    }

    const hostDefault = getHostDefaultLocale(search)
    const target = parameters.has('simulateHost')
        ? hostDefault
        : (queryLocale ?? readLocalePreference() ?? hostDefault)

    if (!target || target === DEFAULT_LOCALE) {
        return
    }

    const nextPath = buildPathnameForLocale(getPathWithoutLocalePrefix(pathname), target)
    const nextUrl = `${nextPath}${search}${hash}`
    if (`${pathname}${search}${hash}` === nextUrl) {
        return
    }

    window.location.replace(nextUrl)
}
